'use client';

/**
 * ProductActionsCard — horizontal action strip for the PDP overlay.
 *
 * Same data source as the rail's VendorActionsCard (the `vendor_actions`
 * BFF strategy keyed on the parent vendor's semantic_id) but rendered
 * inline as a single-row pill strip for the overlay's wider canvas.
 * The rail's stack-style chrome would waste the horizontal real estate.
 *
 * @see klyde_edge/ui_config/composition/cards/vendors.py — content_key="product_actions"
 * @see klyde_edge/klyde_intelligence/vendors/handler.py — vendor_actions strategy
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas / overlay
 */

import { useMemo, useState } from 'react';
import { Loader2, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api/client';
import { useStrategyData } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useTabStore } from '@/lib/stores/tab-store';
import { switchExplorerMode } from '@/lib/stores/tab-store';
import { useIntentStore } from '@/lib/stores/intent-store';
import { useCardExport } from '@/lib/design/card-export';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { ActionButton, type ActionItem } from '@/components/ui/action-button';
import type { ProductExploreResponse } from '@/lib/api/types';

const VENDOR_ACTIONS_STRATEGY = 'vendor_actions';
const PRODUCT_EXPLORE_STRATEGY = 'graph_product_explore';
const MORE_LIKE_THIS_QUERY_STRATEGY = 'catalog_more_like_this_query';

interface MoreLikeThisQueryResponse {
  success: boolean;
  query_text?: string;
  image_base64?: string | null;
  error?: string | null;
}

interface ActionsResponse {
  actions?: ActionItem[];
}

export function ProductActionsCard() {
  const { entityType, entityId } = useViewportSelection();

  // A product always belongs to a vendor — TRAVERSE product → vendor to get it.
  // The BFF already walks that edge in graph_product_explore (dedup'd with the
  // sibling overlay cards, so no extra round-trip) and ships the vendor's
  // semantic_id on the payload. Resolving from the product, not from how you
  // navigated here,
  // means a product opened from search keeps its actions. Tab/selection is only
  // a fallback for non-product (vendor-context) entries.
  const productEnabled = !!entityId && entityType === 'product';
  const { data: productData, isLoading: productLoading } =
    useStrategyData<ProductExploreResponse>(
      PRODUCT_EXPLORE_STRATEGY,
      productEnabled ? { semantic_id: entityId } : {},
      productEnabled,
    );
  // FullProductDetail names the vendor Actor's semantic_id `vendor_id`
  // (`v.semantic_id as vendor_id` on the BFF). The similar-looking vendor_sid
  // is a different product shape's field — reading it was a silent no-op.
  const productVendorSid = productData?.data?.product?.vendor_id ?? null;

  const activeTabId = useTabStore((s) => s.activeTabId);
  const tabs = useTabStore((s) => s.tabs);
  const activeTab = tabs.find((t) => t.id === activeTabId);
  const vendorFromTab =
    activeTab?.type === 'vendor' ? activeTab.entityId ?? null : null;
  const vendorFromSelection =
    entityType === 'vendor' && entityId ? entityId : null;

  const vendorSid = productVendorSid ?? vendorFromSelection ?? vendorFromTab;
  // Product-specific actions (e.g. more_like_this) need the PRODUCT's own
  // sid, not its vendor's — vendor_actions decides what to offer per
  // entity type (PT_ vs CO_ prefix). Vendor-context views (no product
  // selected) still pass the vendor sid, unchanged.
  const actionsSemanticId = productEnabled && entityId ? entityId : vendorSid;
  const enabled = !!actionsSemanticId;

  const { data, isLoading, error } = useStrategyData<ActionsResponse>(
    VENDOR_ACTIONS_STRATEGY,
    enabled ? { semantic_id: actionsSemanticId ?? '' } : {},
    enabled,
  );

  const actions = data?.data?.actions ?? [];
  const seedSearch = useIntentStore((s) => s.seedSearch);
  const [moreLikeThisLoading, setMoreLikeThisLoading] = useState(false);

  const handleMoreLikeThis = async () => {
    if (!productEnabled || !entityId || moreLikeThisLoading) return;
    setMoreLikeThisLoading(true);
    try {
      const res = await api.getStrategyData<MoreLikeThisQueryResponse>(
        MORE_LIKE_THIS_QUERY_STRATEGY,
        { semantic_id: entityId },
      );
      if (!res.success || !res.data?.success || !res.data.query_text) {
        toast.error(res.data?.error || res.error || 'Could not build a similar-products search');
        return;
      }
      // switchExplorerMode's clear-stale-selection step must run before
      // seedSearch — same ordering rule as every other mode-switch +
      // state-handoff pair in this codebase.
      switchExplorerMode('mode3');
      seedSearch({
        text: res.data.query_text,
        images: res.data.image_base64 ? [res.data.image_base64] : [],
      });
    } finally {
      setMoreLikeThisLoading(false);
    }
  };

  // Scene export — overlay (PDP) surface. Distinct payload key from the
  // explore-based siblings: this strip is keyed on the parent vendor's
  // vendor_actions strategy, not graph_product_explore.
  useCardExport(useMemo(() => ({
    label: 'Actions',
    content_key: 'product_actions',
    surface: 'overlay' as const,
    status: (data ? 'loaded' : enabled ? 'loading' : 'idle') as 'loading' | 'loaded' | 'idle',
    query_key: enabled
      ? ['strategy-data', VENDOR_ACTIONS_STRATEGY, { semantic_id: actionsSemanticId ?? '' }]
      : null,
    text: actions.length ? actions.map((a) => a.label).join(' · ') : 'Actions',
    json: { actions: actions.map((a) => ({ key: a.key, label: a.label, disabled: !!a.disabled })) },
  }), [data, actions, enabled, actionsSemanticId]));

  // Busy while the product (for its vendor) or the actions are in flight, so we
  // don't flash "No vendor context" before product → vendor resolves.
  if (productLoading || (enabled && isLoading)) {
    return (
      <div className={cn(text.body, 'h-full flex items-center gap-2 text-muted-foreground')}>
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading actions…
      </div>
    );
  }

  if (!enabled) {
    return (
      <div className={cn(text.body, 'h-full flex items-center gap-2 text-muted-foreground')}>
        <Zap className="h-3.5 w-3.5" />
        No vendor context.
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn(text.body, 'h-full flex items-center text-destructive')}>
        Actions failed: {(error as Error).message}
      </div>
    );
  }

  if (actions.length === 0) {
    return (
      <div className={cn(text.body, 'h-full flex items-center gap-2 text-muted-foreground')}>
        <Zap className="h-3.5 w-3.5" />
        No actions available.
      </div>
    );
  }

  return (
    // Content-height: a compact button row in the scroll-stack, not an
    // h-full band that centers the buttons and leaves whitespace around them.
    <div className="flex items-center gap-2 overflow-x-auto py-0.5">
      {actions.map((a) => (
        <ActionButton
          key={a.key}
          action={a}
          className="shrink-0"
          onClick={a.key === 'more_like_this' ? handleMoreLikeThis : undefined}
          loading={a.key === 'more_like_this' ? moreLikeThisLoading : undefined}
        />
      ))}
    </div>
  );
}
